import React from 'react'
import {useSessionCookie} from "../hooks/useSessionCookie";
import {createContext, ReactNode, useEffect} from 'react'
import axios, {AxiosError, AxiosInstance} from 'axios'
import {useFeedback} from "../hooks/useFeedback"
import {API_HOST} from "../constants"
import {appHost, productionMode} from "../utils/routes"
import {auth} from "../firebase"
import {useTranslation} from "react-i18next"
import {ApiErrorResponse} from "../models/request"


export const IApiServiceContext = createContext<ApiServiceContextState>({})

export type ApiServiceContextState = {
    client?: (withAuth?: boolean, handleFeedback?: boolean) => Promise<AxiosInstance>
}

const ApiServiceContext = (props: { children: ReactNode }) => {
    const {userSession, clearUserSession} = useSessionCookie()
    const feedback = useFeedback()
    const {t} = useTranslation()

    /**
     * Resolve an API error into the text shown to the user.
     *
     * The server sends a stable `messageCode` alongside its own worded `message`.
     * The code wins because it resolves to a translation in the user's active
     * language, and the server message rides along as i18next's `defaultValue`
     * — so a code this client has no translation for still renders the server's
     * wording rather than leaking the raw code on screen. A transport failure
     * carries neither, and falls back to the generic message.
     *
     * A field-level code is looked up as `<CODE>_<field>` before the bare code,
     * so a field whose generic sentence reads badly in some language can carry
     * its own wording (see `FIELD_NOT_UNIQUE_email`, which avoids interpolating
     * the raw schema key into a French sentence). Adding the override is a
     * client-only change: the server keeps sending the same code and field.
     */
    const resolveErrorMessage = (error: AxiosError): string => {
        const response = error.response?.data as ApiErrorResponse | undefined
        const universalMessage = response?.message || t('INTERNAL_ERROR')

        if (!response?.messageCode)
            return universalMessage

        const keys = response.field
            ? [`${response.messageCode}_${response.field}`, response.messageCode]
            : [response.messageCode]

        return t(keys, {
            defaultValue: universalMessage,
            field: response.field
        })
    }

    const client = (withAuth: boolean, handleFeedback?: boolean): Promise<AxiosInstance> =>
        new Promise<AxiosInstance>(async (resolve, reject) => {
                const client = axios.create({
                    baseURL: API_HOST
                })

                // Attach interceptor once
                client.interceptors.response.use(
                    (response) => response,

                    (error: AxiosError) => {
                        console.log(JSON.stringify(error.response?.data))
                        if (handleFeedback)
                            feedback.showFeedback(resolveErrorMessage(error), 'danger')

                        return Promise.reject(error)
                    }
                )
            if (withAuth) {
                // Read the restored sign-in once. A lasting onAuthStateChanged listener per request
                // piled up, and fired in every open tab whenever Firebase reported a transient
                // sign-out — deleting the session cookie all tabs share.
                await auth.authStateReady()
                const user = auth.currentUser
                if (!user) {
                    // The session cookie is shared with the app subdomain. The guest site often has
                    // no Firebase sign-in of its own (people sign in on the app), so clearing it here
                    // would log them out of the app too; only the app clears it.
                    if (!(productionMode && !appHost))
                        clearUserSession()
                    return reject(new Error("Not signed in"))
                }
                client.defaults.headers.common['Authorization'] = await user.getIdToken()
                resolve(client)
            } else
                resolve(client)
            }
        )

    return <IApiServiceContext.Provider value={{
        client
    }}>
        {props.children}
    </IApiServiceContext.Provider>
}

export default ApiServiceContext